import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Form from '../../../../common/form'
import { ForgotLink, Social, SocialLogin } from './components'

import { useLanguage } from '../../../../../data/state/hooks/language'
import { useAuth } from '../../../../../data/hooks/auth'
import { useAuthForms } from '../../../../common/custom-dialogs/auth'

import { UserWithTioken } from '../../../../../data/auth/data-types'
import { registerEmail } from '../../../../../data/auth/register'
import { loginApple, loginFacebook } from '../../../../../data/auth/login'
import ImagePicker from '../../../../common/image-picker'
import OnboardingDialog from '../../../../common/custom-dialogs/onboarding'
import { useDialog } from '../../../../common/dialog/hook'

type RegisterFormProps = {
    onSuccess: () => void
    isDialog: boolean
}

const RegisterForm: React.FunctionComponent<RegisterFormProps> = ({ onSuccess, isDialog }) => {
    const { t } = useTranslation("authorization")
    const { full, main } = useLanguage()
    
    const { showLoginForm } = useAuthForms(isDialog, onSuccess)
    const { user, setAuth } = useAuth()
    const { setComponent } = useDialog()

    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [first_name, setFirstName] = useState("")
    const [last_name, setLastName] = useState("")
    const [picture, setPicture] = useState<File | null>(null)

    const [error, setError] = useState<{ [key: string]: string | null }>({})

    const setFieldError = (error: string, fields?: string[]) => {
        if (fields == null) {
            setError({ '_common': error })
            return
        }

        const errors = fields.reduce((acc, el) => ({ ...acc, [el]: error }), {})
        setError(errors)
    }

    if (user != null) {
        setTimeout(() => {
            onSuccess()
            setComponent(<OnboardingDialog />, undefined, undefined, true)
        }, 100)
    }

    return (
        <Form
            title={t("registration.title")}
            description="Welcome to the community. Let's get to know each other!"
            header={
                <ImagePicker
                    placeholderImage={require("./images/profile.jpg")}
                    onPictureSelected={setPicture}
                />
            }
            inputs={
                [
                    { type: "text", placeholder: t("labels.first_name"), name: "first_name", onChange: ev => setFirstName(ev.target.value), error: error["first_name"] || error["_common"] },
                    { type: "text", placeholder: t("labels.last_name"), name: "last_name", onChange: ev => setLastName(ev.target.value),  error: error["last_name"] || error["_common"]  },
                    { type: "email", placeholder: t("labels.email"), name: "email", onChange: ev => setEmail(ev.target.value),  error: error["email"] || error["_common"]  },
                    { type: "password", placeholder: t("labels.password"), name: "password", onChange: ev => setPassword(ev.target.value),  error: error["password"] || error["_common"]  },
                ]
            }
            button_name={t("registration.register")}
            button_on_tap={() => signUpEmail(first_name, last_name, email, password, picture, full, (token) => setAuth(token.user, token.token), setFieldError)}
        >
            <ForgotLink onClick={showLoginForm}>{t("registration.button_login")}</ForgotLink>   
            <Social>
                <SocialLogin 
                    src={require('./images/social/facebook.png')} 
                    onClick={() => signUpFacebook(full, token => setAuth(token.user, token.token), setFieldError)}
                />
                <SocialLogin 
                    src={require('./images/social/apple.png')}
                    onClick={() => signUpApple(full, token => setAuth(token.user, token.token), setFieldError)}
                />
            </Social>
        </Form>
    )
}

const signUpEmail = async (
    first_name: string,
    last_name: string,
    email: string, 
    password: string, 
    picture: Blob | null,
    language: string,
    onSuccess: (token: UserWithTioken)=>void, onError: (error: string, field?: string[])=>void) => {
    try {
        const { userToken, error } = await registerEmail(first_name, last_name, email, password, picture, language)
        console.log(error)
        if (error != null) {
            console.log('Error registering', error)
            if (error === 'user already exists') {
                onError('User already exists', ['email'])
                return
            }

            if (error === 'email validation error') {
                onError('Invalid email, should be name@host.domain', ['email'])
                return
            }

            if (error === 'password validation error') {
                onError('Insecure password, should be at least 6 characters', ['password'])
                return
            }

            if (error === 'bad param') {
                onError('First and last names should be at least 2 characters', ['first_name', 'last_name'])
                return
            }

            onError('Unexpected error, try again later', ['password'])
            return
        }

        console.log(userToken)
        onSuccess(userToken!)
    } catch (e) {
        console.log(e)
    }
}

const signUpFacebook = async (language: string, onSuccess: (token: UserWithTioken)=>void, onError: (error: string, field?: string[])=>void) => {
    try {
        // @ts-ignore
        window.FB.login(async (response) => {
            const { authResponse } = response

            if (!authResponse) {
                console.log('User cancelled register or did not fully authorize.')
                console.log({ response })
                return
            }

            const { userToken, error } = await loginFacebook(authResponse.accessToken, language)
            if (error != null) {
                console.log('Error registering', error)
                if (error === 'user already exists') {
                    onError('User already exists', ['email'])
                    return
                }
    
                if (error === 'email validation error') {
                    onError('Invalid email, should be name@host.domain', ['email'])
                    return
                }   
    
                if (error === 'bad param') {
                    onError('First and last names should be at least two symbols', ['first_name', 'last_name'])
                    return
                }
    
                onError('Unexpected error, try again later', ['password'])
                return
            }

            console.log(userToken)

            onSuccess(userToken!)
        })
    } catch (e) {
        console.log(e)
        onError("Something went wrong, try again later")
    }
}

const signUpApple = async (language: string, onSuccess: (token: UserWithTioken)=>void, onError: (error: string, field?: string[])=>void) => {
    try {
        // @ts-ignore
        const response = await window.AppleID.auth.signIn()

        console.log({ response })

        if (response.error != null) {
            onError(response.error)
            return
        }

        const { authorization: { id_token } } = response

        const { userToken, error } = await loginApple(id_token, language)
        
        if (error != null) {
            console.log('Error registering', error)
            if (error === 'user already exists') {
                onError('User already exists', ['email'])
                return
            }

            if (error === 'email validation error') {
                onError('Invalid email, should be name@host.domain', ['email'])
                return
            }

            if (error === 'bad param') {
                onError('First and last names should be at least two symbols', ['first_name', 'last_name'])
                return
            }

            onError('Unexpected error, try again later', ['password'])
            return
        }

        console.log(userToken)

        onSuccess(userToken!)
    } catch (e) {
        console.log(e)
        onError("Something went wrong, try again later")
    }
}


export default RegisterForm